import customFetch from "./helper"
import { User, UserParams } from "@/types/users"
import { removeActiveBlogId, removeJwtCookies } from "../../util/cookies"
import { Blog, ErrorResponse, Subscription } from "@types"
import type { SIWESession } from "connectkit"
import { identifySentryUser, identifySentryWallet } from "../utils/sentry"

const API = "/users"

/**
 * Fetches the logged-in user.
 *
 * Also "refreshes" their JWT, to keep them logged in.
 */
export async function getLoggedInUser(): Promise<User> {
  const response = await customFetch<User>(
    `${API}/refresh`,
    {},
    {
      // Don't care about impersonation here.
      ignoreImpersonation: true,
    }
  )

  if (response) {
    identifySentryUser(response)
  }

  return response
}

export async function loginUser(
  userParams: UserParams
): Promise<User | ErrorResponse> {
  try {
    const response = await customFetch<User>(`${API}/login`, {
      method: "POST",
      body: JSON.stringify(userParams),
      headers: {
        "Content-Type": "application/json",
      },
    })

    console.log("Login response: ", response)

    return response
  } catch (err: any) {
    if (err?.response?.status === 400) {
      return {
        msg: "Incorrect email or password. Please try again.",
      }
    }
  }

  return { msg: "Incorrect email or password. Please try again." }
}

/**
 * Logs the user out.
 * This just deletes their JWT.
 * We need to redirect them on the FE if we wish.
 */
export async function logoutUser(): Promise<User> {
  const response = await customFetch<User>(`${API}/logout`, { method: "POST" })

  removeJwtCookies()
  removeActiveBlogId()

  return response
}

/**
 * For the logged in user, fetches all blogs
 * they're a team member of.
 */
export async function getUsersTeamBlogs(): Promise<Array<Blog>> {
  const response = await customFetch<Array<Blog>>(`${API}/team/blogs`)

  return response
}

export async function getWalletSession(): Promise<SIWESession> {
  const res = await customFetch<SIWESession>(`${API}/siwe/session`)
  if (res && res.address) {
    identifySentryWallet(res.address)
  }
  return res
}

export async function generateNonce(): Promise<string> {
  const res = await customFetch<{ nonce: string }>(`${API}/siwe/nonce`, {
    method: "POST",
  })
  return res.nonce
}

export async function verifyNonce({
  wallet_address,
  signature,
  ens,
  message,
}: {
  wallet_address: string
  signature: string
  ens?: string
  message: string
  referrerWalletAddress?: string
}): Promise<User> {
  const payload = {
    wallet_address,
    signature,
    ens,
    message,
  }

  const res = await customFetch<User>(`${API}/siwe/verify`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  })

  return res
}

export async function setPasswordAndLoginUser(
  userParams: UserParams
): Promise<User | ErrorResponse> {
  try {
    const response = await customFetch<User>(`${API}/setPasswordAndLogin`, {
      method: "POST",
      body: JSON.stringify(userParams),
      headers: {
        "Content-Type": "application/json",
      },
    })

    // TODO: If the user owns a custom domain (eg avc.xyz), we should ideally set the cookie there too, even if they're currently logging on on paragraph.xyz
    await customFetch<User>(`${API}/login`, {
      method: "POST",
      body: JSON.stringify(userParams),
      headers: {
        "Content-Type": "application/json",
      },
    })

    return response
  } catch (err: any) {
    if (err?.response?.status === 400) {
      return {
        msg: "Email or password is incorrect",
      }
    }
    return { msg: "An error occurred setting password and logging in." }
  }
}

/**
 * Fetches a user's subscription to a blog.
 */
export async function getUsersSubscriptionToBlog(
  blogId: string
): Promise<Subscription | false | ErrorResponse> {
  const response = await customFetch<Subscription | false | ErrorResponse>(
    `${API}/subscriptions/${blogId}`,
    {},
    {
      ignoreImpersonation: true,
    }
  )

  return response
}
